/**
 * useRoles + role-management mutations — extracted from `use-users.ts`
 * per issue #134 (split monolithic Users hook).
 *
 * Covers:
 *   - Role catalog: useRoles, RoleRow
 *   - Role CRUD: useCreateRole, useUpdateRole, useDeleteRole
 *   - Per-user role assignments: useUserRoleAssignments,
 *     useAssignRoleToUser, useUnassignRoleFromUser
 *
 * The shapes + behaviour are unchanged from the pre-split version. All
 * consumers continue to import from `lib/use-users` (which re-exports
 * everything here) — direct imports from `lib/use-roles` are also
 * supported for new code.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type { PermissionCatalogueEntrySchema } from '@argusai/shared/schemas/permissions';
import { apiClient } from './api-client';
// B2e-T2 / review RV-01 — the shell's grant set lives in AuthProvider's state,
// not in the query cache, so the ONE way to refresh it is this context's
// `refresh()`. (No cycle: auth-context imports `lib/api-client` and nothing
// from this file or from `lib/use-users`, which re-exports it.)
import { useAuth } from '@/contexts/auth-context';

// ─── W1a-07 — Roles tab + custom-role management ───────────────────────────

/**
 * Wire-shape for a single role row from `GET /api/v1/users/roles`.
 *
 * ONE TABLE, TWO KINDS (B1-T4). The list returns the SHIPPED roles and the
 * tenant's own, and both are rows of `roles`:
 *
 *   `kind: 'platform'` — shipped by the release, READ-ONLY (founder decision
 *                        C5). Clone it to get an editable copy; there is no
 *                        per-tenant override row and no third kind.
 *   `kind: 'custom'`   — the tenant's own. Editable, retirable, clonable.
 *
 * `type: 'system' | 'custom'` and `builtin` USED TO STAND HERE and are gone
 * with the `system:<role>` sentinel ids they described: the API synthesised
 * four rows in code from a hardcoded list, so the screens and the runtime gate
 * read different stores and the fifth shipped role (`power_user`) appeared on
 * neither. Every row has a real id and a real `key` now — reference by key,
 * never by name (principle #5).
 *
 * `permissionCount` is the size of `permissions` as the SERVER computed it;
 * the screen prints it rather than counting an array it was handed, which is
 * the rule B1b closed and B1-T4 keeps closed.
 *
 * `assignedUserCount` counts PEOPLE the resolver would honour: deleted,
 * deactivated and departed users excluded, roles held through a GROUP
 * included.
 */
export interface RoleRow {
  id: string;
  /** The stable machine name (`owner`, `admin`, `level_2_approver`). */
  key: string;
  name: string;
  kind: 'platform' | 'custom';
  description: string | null;
  permissions: Array<{ action: string; resource: string }>;
  permissionCount: number;
  assignedUserCount: number;
  /**
   * Has this role EVER been assigned — every row on file, including one
   * belonging to a user who has since been deleted.
   *
   * It is NOT `assignedUserCount > 0` and must never be shown as a count: it
   * is the fact `DELETE /users/roles/:id` chooses from, so it is what the
   * delete dialog has to read to say whether the role will be RETIRED or
   * removed. Predicting that from the live holder count told the operator the
   * opposite of what happened whenever the only holder had been deleted
   * (review B1-T4-R2 / B1T4-04).
   */
  everAssigned: boolean;
  /** Set when a tenant role has been retired; shipped rows are never retired. */
  retiredAt: string | null;
  /** For a clone: the key of the role it came from. */
  basedOnKey: string | null;
  createdAt: string | null;
}

interface RolesListResponse {
  data: RoleRow[];
}

/**
 * Fetch the role catalog (system + custom) for the caller's tenant.
 *
 * Query key: `['roles']`. Mutations (create/update/delete/assign/unassign)
 * invalidate this key so all consumers refetch.
 */
export function useRoles(): {
  roles: RoleRow[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery<RolesListResponse>({
    queryKey: ['roles'],
    queryFn: () => apiClient.get<RolesListResponse>('/users/roles'),
    staleTime: 60_000,
  });

  return {
    roles: data?.data ?? [],
    isLoading,
    error,
    refetch,
  };
}

/**
 * Body for `POST /api/v1/users/roles`. Server enforces:
 *   - `name`: 1-100 chars, [a-zA-Z0-9_- ]+, NOT a reserved system role name
 *   - `permissions`: ≥1 grant, each (action, resource) is a known catalog entry
 *   - PRIVILEGE-ESCALATION GUARD: every grant must be one the caller has
 *     (else 403 PRIVILEGE_ESCALATION with `details.violations`)
 */
export interface CreateRoleBody {
  name: string;
  description?: string | null;
  permissions: Array<{ action: string; resource: string }>;
}

/**
 * Create a custom role — wires to W1a-07's POST /users/roles.
 *
 * Returns the created RoleRow on success. On failure the error message
 * carries the server's response shape (PRIVILEGE_ESCALATION /
 * NAME_CONFLICT / VALIDATION_ERROR).
 */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation<RoleRow, Error, CreateRoleBody>({
    mutationFn: (body) => apiClient.post<RoleRow>('/users/roles', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * Body for `PATCH /api/v1/users/roles/:id`. All fields optional, but at
 * least one must be present (else 422 VALIDATION_ERROR). System roles
 * (id starts with `system:`) → 403 FORBIDDEN.
 */
export interface UpdateRoleBody {
  name?: string;
  description?: string | null;
  permissions?: Array<{ action: string; resource: string }>;
}

/**
 * Update a custom role — wires to W1a-07's PATCH /users/roles/:id.
 *
 * No-op short-circuit: if every field equals the existing value, the
 * server returns 200 with the existing row but emits NO new audit row.
 */
export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation<RoleRow, Error, { roleId: string; body: UpdateRoleBody }>({
    mutationFn: ({ roleId, body }) =>
      apiClient.patch<RoleRow>(`/users/roles/${roleId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * Remove a tenant role — wires to DELETE /users/roles/:id.
 *
 * RETIRE OR DELETE, decided by the server (B1-T4): a role that has ever been
 * assigned is RETIRED (`retired: true`) so its history survives; one that
 * never was is deleted outright. A shipped role answers 403.
 *
 * It no longer answers `409 ROLE_IN_USE`. That refusal counted assignment rows
 * with no liveness test, so an assignment held by a user deleted months ago
 * blocked the delete for ever and the screen could not show the blocker —
 * a refusal the operator had no way to act on.
 */
export interface RoleRemovalResult {
  id: string;
  deleted: boolean;
  retired: boolean;
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation<RoleRemovalResult, Error, { roleId: string }>({
    mutationFn: ({ roleId }) =>
      apiClient.delete<RoleRemovalResult>(`/users/roles/${roleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      // An assignment count drops when role is deleted (cascade); refresh
      // any user-detail role lists too.
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Body for `POST /api/v1/users/roles/:id/clone`. Both fields optional: the
 * server names the clone `<source> (copy)` and copies the source's description
 * when they are omitted.
 */
export interface CloneRoleBody {
  name?: string;
  description?: string | null;
}

/**
 * Clone a role — the way a tenant changes a SHIPPED role (founder decision
 * C5: "shipped role stays as is — tenant can make more if he needs to").
 *
 * The clone is an ordinary tenant role carrying `basedOnKey`, so the screen
 * can say where it came from. The server runs the same escalation guard it
 * runs on create: a caller cannot mint a role granting more than they hold.
 */
export function useCloneRole() {
  const qc = useQueryClient();
  return useMutation<RoleRow, Error, { roleId: string; body?: CloneRoleBody }>({
    mutationFn: ({ roleId, body }) =>
      apiClient.post<RoleRow>(`/users/roles/${roleId}/clone`, body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

// ─── B1-T4 — a GROUP holds roles ───────────────────────────────────────────
//
// `group_role_assignments` has been honoured by the resolver since B1-T2, but
// nothing could write it: the only INSERT was in an unregistered route file.
// These three wire the group's Permissions tab to the endpoints that can.

/** One role a group holds, as `GET /users/groups/:id/permissions` reports it. */
export interface GroupRoleRow {
  roleId: string;
  roleKey: string;
  roleName: string;
  kind: 'platform' | 'custom';
  permissionCount: number;
  assignedAt: string | null;
}

/** One grant the group hands its members, with the role it came from. */
export interface GroupGrantRow {
  key: string;
  action: string;
  resource: string;
  source: string;
}

export interface GroupPermissionsResponse {
  groupId: string;
  roles: GroupRoleRow[];
  permissions: GroupGrantRow[];
}

/**
 * What a group grants — computed by the SAME resolver path that decides what
 * its members may do, never by the browser unioning role rows.
 */
export function useGroupPermissions(groupId: string | null): {
  data: GroupPermissionsResponse | null;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<GroupPermissionsResponse>({
    queryKey: ['groups', groupId, 'permissions'],
    queryFn: () =>
      apiClient.get<GroupPermissionsResponse>(`/users/groups/${groupId}/permissions`),
    enabled: !!groupId,
  });
  return { data: data ?? null, isLoading, error };
}

export function useAssignRoleToGroup() {
  const qc = useQueryClient();
  return useMutation<
    { groupId: string; roleId: string; roleKey: string; roleName: string; assignedAt: string },
    Error,
    { groupId: string; roleId: string }
  >({
    mutationFn: ({ groupId, roleId }) =>
      apiClient.post(`/users/groups/${groupId}/roles`, { roleId }),
    onSuccess: (_data, { groupId }) => {
      qc.invalidateQueries({ queryKey: ['groups', groupId, 'permissions'] });
      // The role's holder count moves with the group's membership.
      qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useRemoveRoleFromGroup() {
  const qc = useQueryClient();
  return useMutation<
    { groupId: string; roleId: string; removed: boolean },
    Error,
    { groupId: string; roleId: string }
  >({
    mutationFn: ({ groupId, roleId }) =>
      apiClient.delete(`/users/groups/${groupId}/roles/${roleId}`),
    onSuccess: (_data, { groupId }) => {
      qc.invalidateQueries({ queryKey: ['groups', groupId, 'permissions'] });
      qc.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export interface RoleAssignmentResponse {
  userId: string;
  roleId: string;
  roleName: string;
  assignedAt: string;
}

/**
 * Assign a custom role to a user — wires to W1a-07's
 * POST /users/:userId/roles.
 *
 * Server guards:
 *   - Self-assignment forbidden (separation-of-duties)
 *   - Caller must currently have every permission the role grants
 *     (PRIVILEGE_ESCALATION 403 if not)
 *   - Already-assigned: 409 ALREADY_ASSIGNED
 *   - System role assignment routes through W1a-04 PATCH /users/:id instead
 */
export function useAssignRoleToUser() {
  const qc = useQueryClient();
  return useMutation<RoleAssignmentResponse, Error, {
    userId: string;
    roleId: string;
  }>({
    mutationFn: ({ userId, roleId }) =>
      apiClient.post<RoleAssignmentResponse>(
        `/users/${userId}/roles`,
        { roleId }
      ),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
      // Closes #217 + #222: the user-detail Roles & Groups tab is
      // hydrated by the role-assignments query; without invalidation
      // the new assignment didn't show its permissionCount until
      // navigate-away-and-back.
      qc.invalidateQueries({ queryKey: ['users', userId, 'role-assignments'] });
    },
  });
}

/**
 * Unassign a custom role from a user — wires to W1a-07's
 * DELETE /users/:userId/roles/:roleId.
 *
 * Same guards as assign (self-action forbidden, system roles go elsewhere).
 * 404 NOT_FOUND if no active assignment exists.
 */
export function useUnassignRoleFromUser() {
  const qc = useQueryClient();
  return useMutation<{ userId: string; roleId: string; unassigned: true }, Error, {
    userId: string;
    roleId: string;
  }>({
    mutationFn: ({ userId, roleId }) =>
      apiClient.delete<{ userId: string; roleId: string; unassigned: true }>(
        `/users/${userId}/roles/${roleId}`
      ),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
      // Closes #217: role-assignments query feeds the user detail
      // Roles & Groups tab; without this invalidation the just-removed
      // custom role would linger until the user navigated away and
      // back.
      qc.invalidateQueries({ queryKey: ['users', userId, 'role-assignments'] });
    },
  });
}

/** What `POST /users/:id/transfer-ownership` answers with. */
export interface OwnershipTransferResult {
  organizationId: string;
  /** `roleName` is the store's display name — the browser prints it rather
   *  than translating a key, which would be a role compare in packages/web. */
  previousOwner: { id: string; email: string; roleKey: string; roleName: string };
  newOwner: { id: string; email: string; roleKey: string; roleName: string };
  /** Live Owners the transaction committed with. One, by design. */
  ownerCount: number;
}

/**
 * Hand the tenant to somebody else — `POST /users/:id/transfer-ownership`.
 *
 * Foundations 2 (Users rail), B2e-T2. Founder, 2026-09-18: ownership
 * TRANSFERS, it does not duplicate. The subject becomes Owner and the CALLER
 * becomes Admin in one transaction, so a tenant holds exactly one Owner; the
 * outgoing Owner keeps full administrative power.
 *
 * `confirm` is the tenant's own name, and the browser does not invent it: it
 * is `user.ownershipTransfer.confirmPhrase` off `GET /users/:id`, which is the
 * string the server compares against. A phrase assembled on this side would be
 * a 422 the dialog could not have predicted.
 *
 * THE CALLER'S OWN AUTHORITY CHANGES, so this refreshes more than the row it
 * touched: `/users/me` carries the grant set the whole shell gates on, and
 * after a transfer it is an Admin's, not an Owner's. Leaving it stale would
 * leave every Owner-only affordance on screen until the next reload — offering
 * exactly the actions the gateway has just started refusing.
 *
 * AND IT REFRESHES IT THROUGH THE ONE THING THAT HOLDS IT (review B2eT2-RV-01).
 * That paragraph used to be followed by `invalidateQueries(['me'])` and
 * `['users','me']`, which was inert: the shell's grant set does not live in a
 * query cache at all. `AuthProvider` calls `apiClient.get('/users/me')` itself
 * and keeps the answer in `useState`, exposing the re-read as `refresh()`
 * (contexts/auth-context.tsx) — so `['me']` matched no query, and `can()`,
 * `useHasPermission` and every role display in the shell went on answering as
 * Owner. The hook awaits `refresh()` instead, here rather than at the call
 * site, so a second caller cannot forget it: one remedy, in the hook that
 * causes the staleness.
 */
export function useTransferOwnership() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  return useMutation<
    OwnershipTransferResult,
    Error,
    { userId: string; confirm: string }
  >({
    mutationFn: ({ userId, confirm }) =>
      apiClient.post<OwnershipTransferResult>(
        `/users/${userId}/transfer-ownership`,
        { confirm }
      ),
    onSuccess: async (data, { userId }) => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['users', userId] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'audit'] });
      qc.invalidateQueries({ queryKey: ['users', userId, 'role-assignments'] });
      // The outgoing Owner's own page.
      qc.invalidateQueries({ queryKey: ['users', data.previousOwner.id] });
      // THEIR OWN AUTHORITY — not a query key, because it is not a query. This
      // re-reads `/users/me` into the AuthProvider state the shell gates on,
      // and it is AWAITED so the dialog's success path cannot close over an
      // Owner's grant set (review B2eT2-RV-01).
      await refresh();
    },
  });
}

// ─── Per-user role assignment (read) — was in use-users.ts ──────────────────

export interface UserRoleAssignment {
  assignmentId: string;
  /**
   * The role the ASSIGNMENT ROW names — the id the Remove control sends to
   * `DELETE /users/:userId/roles/:roleId`, which matches
   * `user_role_assignments.role_id`. For a retired role re-pointed at a
   * successor this is the RETIRED role (B1-T2c review R1 / BF-01).
   */
  roleId: string;
  /** The role whose grants are in force — the successor, when re-pointed. */
  effectiveRoleId: string;
  roleName: string;
  roleDescription: string | null;
  roleType: 'system' | 'custom';
  permissionCount: number;
  source: string | null;
  /** ISO 8601, or null when the row carried no readable timestamp. */
  assignedAt: string | null;
  assignedBy: { id: string | null; email: string | null } | null;
  expiresAt: string | null;
}

/**
 * Fetch user_role_assignments rows for one user. Backed by the new
 * `GET /users/:id/role-assignments` endpoint (commit ad4b7f4 — closes
 * prior audit CM-3). The org-membership system role is on the user
 * payload's `role` field; this hook surfaces the additional
 * custom-role assignments.
 */
export function useUserRoleAssignments(userId: string | undefined): {
  assignments: UserRoleAssignment[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<{ data: UserRoleAssignment[] }>({
    queryKey: ['users', userId, 'role-assignments'],
    queryFn: () =>
      apiClient.get<{ data: UserRoleAssignment[] }>(
        `/users/${userId}/role-assignments`
      ),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return {
    assignments: data?.data ?? [],
    isLoading,
    error,
  };
}

// ─── Effective permissions — read from the ONE answer on the server ─────────

/**
 * One row of `GET /users/:id/permissions`.
 *
 * `actions` is every action granted on the resource, verbatim — the four CRUD
 * booleans cannot carry `invite:users`, `export:users`, `role:assign:users`,
 * `mfa:reset:users` or `lifecycle:*`, and dropping them is half of what made
 * the page disagree with the gate (B1-T2c).
 *
 * `source` is the roles and groups that carried the grants, already
 * de-duplicated by the server: `role:<Name>` / `group:<Name>`, comma-separated.
 */
export interface EffectivePermissionRow {
  resource: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  actions: string[];
  source: string;
}

/**
 * The effective permissions of one user, as the SERVER resolves them.
 *
 * B1-T2c review BF-02. The Effective Permissions tab used to compute this in
 * the browser: it unioned the grants of whatever entries of the `useRoles()`
 * catalogue it could match to `user.roles` by name, which is the "browser's own
 * copy of the permission matrix" docs/system/rails/users.md says must not
 * exist. It could not see an expired assignment, an inactive or retired role,
 * a group-held role or a non-CRUD grant, so the page showed authority
 * `requirePermission` refuses and hid authority it grants.
 *
 * `GET /users/:id/permissions` is a projection of `resolvePrincipal` — the same
 * function the gate reads — so the page and the gate now answer from one read.
 * There is deliberately NO fallback list: if the request fails, the tab says so
 * rather than inventing a plausible one (nothing fails open, principles.md #4).
 */
export function useUserEffectivePermissions(userId: string | undefined): {
  permissions: EffectivePermissionRow[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<{ data: EffectivePermissionRow[] }>({
    queryKey: ['users', userId, 'permissions'],
    queryFn: () =>
      apiClient.get<{ data: EffectivePermissionRow[] }>(
        `/users/${userId}/permissions`,
      ),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return { permissions: data?.data ?? [], isLoading, error };
}

// ─── The permission catalogue — served, never mirrored ──────────────────────

/**
 * One row of `GET /users/permissions/catalogue`.
 *
 * Every key the release recognises, with what a screen needs to draw it. The
 * server orders the array by group, then resource, then action, so the wire
 * order IS the render order and no screen sorts.
 *
 * INFERRED FROM THE SERVER'S OWN SCHEMA (review B1T3-R3). This used to be a
 * hand-kept TypeScript `interface` beside a hand-kept `z.object` in
 * `routes/v1/users.ts`, so renaming `highRisk` on the server left both
 * packages type-checking while the picker's ⚠ column silently emptied. Zod is
 * the type source (CLAUDE.md); the schema is the one declaration and this is a
 * view of it, so a rename is a compile error here.
 */
export type PermissionCatalogueRow = z.infer<
  typeof PermissionCatalogueEntrySchema
>;

/**
 * THE permission catalogue, from the server.
 *
 * Foundations 1 (Users rail), B1-T3. The Roles page used to hold its own: a
 * 13-action x 12-resource grid hand-written in `routes/users/roles.tsx`, plus a
 * second, differently-shaped copy in `components/users/role-editor-dialog.tsx`
 * against a vocabulary the server has never used. The real catalogue is 156
 * keys over 21 actions and 52 resources, so an admin could not GRANT
 * `write:tools` — the row was not on the screen — and a role that already held
 * it rendered as holding nothing.
 *
 * There is deliberately NO fallback list. A plausible default here is how the
 * mirrored copy came to exist; when the request fails the picker says so
 * (principles.md #4).
 *
 * WHAT ARRIVES IS THE TENANT SLICE (review SEC-B1b-06, narrowed by prove
 * PROVE-B1b-C2). The route serves `TENANT_PERMISSION_CATALOGUE` — the tenant
 * AUDIENCE, and nothing else — 132 of the 156 keys, because the 24
 * platform-audience keys are the sysadmin control surface and no tenant role
 * can hold one. `factory:reset:tenants` is in the 132: it sits in the Platform
 * GROUP but its group declares it tenant-audience, because the shipped Owner
 * bundle really holds it. `delete:tenants` is not, and used to be: the rule
 * had an exception clause keyed off delegability rather than audience. The
 * browser still renders exactly what it is served and decides nothing.
 */
export function usePermissionCatalogue(): {
  catalogue: PermissionCatalogueRow[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<{ data: PermissionCatalogueRow[] }>({
    queryKey: ['users', 'permissions', 'catalogue'],
    queryFn: () =>
      apiClient.get<{ data: PermissionCatalogueRow[] }>('/users/permissions/catalogue'),
    // A release constant: it cannot change while the tab is open.
    staleTime: 60 * 60_000,
  });
  return { catalogue: data?.data ?? [], isLoading, error };
}

/**
 * The catalogue grouped for rendering, in the order the server sent it.
 *
 * Pure reshaping of the payload — no key is added, dropped or reordered, so the
 * browser is displaying an answer rather than computing one. That rule IS
 * enforced now: `scripts/ci/no-web-derivation-lint.mjs` (B1-T5, in the CI
 * `guards` job) fails the build on a browser-side authority model. Review
 * B1T3-R5 removed the citation while the script was still unwritten; review
 * SEC-B1b-09 put it back, because B1-T5 landed in the same batch and the
 * denial outlived its reason.
 */
export function groupPermissionCatalogue<
  TRow extends { group: string; resource: string },
>(
  catalogue: TRow[],
): Array<{ group: string; resources: Array<{ resource: string; entries: TRow[] }> }> {
  // Generic over the ROW so the read-only matrix can group its widened display
  // rows (the "Other" section, for a key this release does not carry) through
  // the same function. Only `group` and `resource` are read, and nothing is
  // added, dropped or reordered.
  const groups: Array<{
    group: string;
    resources: Array<{ resource: string; entries: TRow[] }>;
  }> = [];
  for (const entry of catalogue) {
    let group = groups.find((g) => g.group === entry.group);
    if (!group) {
      group = { group: entry.group, resources: [] };
      groups.push(group);
    }
    let resource = group.resources.find((r) => r.resource === entry.resource);
    if (!resource) {
      resource = { resource: entry.resource, entries: [] };
      group.resources.push(resource);
    }
    resource.entries.push(entry);
  }
  return groups;
}
